import React, { useRef, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
    Calendar,
    Upload,
    Columns,
    Eye,
    CheckCircle2,
    AlertCircle,
    FileSpreadsheet,
    ShieldCheck,
    PlayCircle,
    Download,
} from "lucide-react";

const FIELDS = [
    { key: "isbn", label: "ISBN", required: true, candidates: ["isbn", "isbn-13", "ean"] },
    { key: "haupttitel", label: "Haupttitel", required: true, candidates: ["haupttitel", "titel", "title", "buchtitel"] },
    { key: "programmplatz", label: "Programmplatz", candidates: ["programmplatz", "programm"] },
    { key: "erloesgruppe", label: "Erlösgruppe", candidates: ["erlösgruppe", "erloesgruppe", "erlösgruppen"] },
    { key: "produkttyp", label: "Produkttyp", candidates: ["produkttyp", "produkt typ", "produkt-typ"] },
    { key: "preis_de", label: "Preis DE", candidates: ["preis de", "preis", "ladenpreis", "vk de"] },
    { key: "lager_oe", label: "Lager Ö", candidates: ["lager ö", "lager oe", "lager österreich"] },
    { key: "lager_de", label: "Lager D", candidates: ["lager d", "lager de", "lager deutschland"] },
    { key: "et", label: "Erscheinungstermin (ET)", candidates: ["et", "erscheinungstermin", "et-datum"] },
    { key: "auflage_nr", label: "Akt. Auflagennummer", candidates: ["auflagennummer", "auflage", "akt. auflage"] },
    { key: "auflage_anlieferung", label: "Akt. Auflage Anlieferung", candidates: ["anliefertermin", "anlieferung", "auflage anlieferung", "aa"] },
    { key: "hk_kal", label: "HK Kal", candidates: ["hk kal", "hk kalkuliert", "hk-kal"] },
    { key: "hk_ist", label: "HK Ist", candidates: ["hk ist", "hk", "hk-ist"] },
    { key: "lieferstatus", label: "Lieferstatus", candidates: ["lieferstatus", "status"] },
    { key: "programmhalbjahr", label: "Programmhalbjahr", candidates: ["programmhalbjahr", "halbjahr"] },
    { key: "bemerkungen", label: "Bemerkungen", candidates: ["bemerkungen", "notizen", "anmerkungen"] },
];

const STEPS = [
    { id: 1, label: "Zeitraum", icon: Calendar },
    { id: 2, label: "Datei", icon: Upload },
    { id: 3, label: "Spalten", icon: Columns },
    { id: 4, label: "Vorschau", icon: Eye },
    { id: 5, label: "Dry-Run", icon: ShieldCheck },
    { id: 6, label: "Import", icon: CheckCircle2 },
];

function autoMap(headers) {
    const lower = headers.map((h) => (h || "").toString().toLowerCase().trim());
    const map = {};
    for (const f of FIELDS) {
        for (const cand of f.candidates) {
            const idx = lower.findIndex((h) => h === cand || h.replace(/\s+/g, "") === cand.replace(/\s+/g, ""));
            if (idx !== -1) {
                map[f.key] = headers[idx];
                break;
            }
            const fuzzy = lower.findIndex((h) => h.includes(cand));
            if (fuzzy !== -1) {
                map[f.key] = headers[fuzzy];
                break;
            }
        }
    }
    return map;
}

export default function ImportWizard({ open, onOpenChange, onImported }) {
    const { activeTenant } = useTenant();
    const [step, setStep] = useState(1);
    const [zeitraumTyp, setZeitraumTyp] = useState("jahr");
    const [zeitraum, setZeitraum] = useState(String(new Date().getFullYear()));
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [mapping, setMapping] = useState({});
    const [result, setResult] = useState(null);
    const [dryRun, setDryRun] = useState(null);
    const [dryRunJobId, setDryRunJobId] = useState(null);
    const [optionAExport, setOptionAExport] = useState(null); // {url, filename, size}
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null); // {processed, total, status, mode: 'dryrun'|'import'}
    const fileRef = useRef(null);

    const reset = () => {
        setStep(1);
        setFile(null);
        setPreview(null);
        setMapping({});
        setResult(null);
        setDryRun(null);
        setDryRunJobId(null);
        setOptionAExport(null);
        setProgress(null);
    };

    const close = (val) => {
        onOpenChange(val);
        if (!val) reset();
    };

    const handleFile = async (f) => {
        if (!f) return;
        setFile(f);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const r = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(r.data);
            setMapping(autoMap(r.data.header));
            setStep(3);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Datei konnte nicht gelesen werden");
        } finally {
            setBusy(false);
        }
    };

    /**
     * Pollt Job-Status alle 500ms bis status != "running". Aktualisiert progress-state.
     * Liefert das fertige Job-Objekt zurück oder wirft bei Fehler.
     */
    const waitForJob = async (jobId, mode) => {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const r = await api.get(`/import/job/${jobId}`);
            const j = r.data;
            setProgress({
                processed: j.processed || 0,
                total: j.total || 0,
                status: j.status,
                mode,
            });
            if (j.status === "done") return j;
            if (j.status === "error") throw new Error(j.error || "Import fehlgeschlagen");
            await new Promise((res) => setTimeout(res, 500));
        }
    };

    const runDryRun = async () => {
        if (!activeTenant) return;
        setBusy(true);
        setProgress({ processed: 0, total: 0, status: "running", mode: "dryrun" });
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("zeitraum", zeitraum);
            fd.append("zeitraum_typ", zeitraumTyp);
            fd.append("column_mapping", JSON.stringify(mapping));
            const r = await api.post("/import/dry-run", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const job = await waitForJob(r.data.job_id, "dryrun");
            setDryRun(job.result);
            setDryRunJobId(r.data.job_id);
            setOptionAExport(null);
            setStep(5);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Dry-Run fehlgeschlagen");
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    const runImport = async () => {
        if (!activeTenant) return;
        setBusy(true);
        setProgress({ processed: 0, total: 0, status: "running", mode: "import" });
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("zeitraum", zeitraum);
            fd.append("zeitraum_typ", zeitraumTyp);
            fd.append("column_mapping", JSON.stringify(mapping));
            const r = await api.post("/import/excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const job = await waitForJob(r.data.job_id, "import");
            setResult(job.result);
            setStep(6);
            toast.success(`Import abgeschlossen: ${job.result.imported_movements} Bewegungen`);
            onImported && onImported();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Import fehlgeschlagen");
        } finally {
            setBusy(false);
            setProgress(null);
        }
    };

    const requiredOk = FIELDS.filter((f) => f.required).every((f) => mapping[f.key]);

    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Excel-Import-Assistent</DialogTitle>
                    <DialogDescription>
                        Importieren Sie Bewegungsdaten für <span className="text-tenant font-medium">{activeTenant?.name}</span>.
                    </DialogDescription>
                </DialogHeader>

                {/* Stepper */}
                <div className="flex items-center justify-between gap-2 py-3 px-1 border-y border-border">
                    {STEPS.map((s, i) => {
                        const active = step === s.id;
                        const done = step > s.id;
                        return (
                            <div key={s.id} className="flex-1 flex items-center gap-2 last:flex-none">
                                <div
                                    className={`h-8 w-8 rounded-full flex items-center justify-center border text-xs font-medium ${
                                        done
                                            ? "bg-tenant text-[color:var(--tenant-accent-foreground)] border-transparent"
                                            : active
                                            ? "border-tenant text-tenant"
                                            : "border-border text-muted-foreground"
                                    }`}
                                >
                                    <s.icon className="h-4 w-4" />
                                </div>
                                <div className={`text-xs uppercase tracking-widest ${active ? "text-foreground" : "text-muted-foreground"}`}>
                                    {s.label}
                                </div>
                                {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border" />}
                            </div>
                        );
                    })}
                </div>

                {/* Step content */}
                <div className="min-h-[280px] py-3 relative">
                    {progress && (
                        <div
                            className="absolute inset-0 z-10 bg-background/90 backdrop-blur-sm flex items-center justify-center"
                            data-testid="import-progress-overlay"
                        >
                            <div className="w-full max-w-md px-6 space-y-4">
                                <div className="flex items-center gap-2 text-tenant">
                                    {progress.mode === "import" ? (
                                        <PlayCircle className="h-5 w-5 animate-pulse" />
                                    ) : (
                                        <ShieldCheck className="h-5 w-5 animate-pulse" />
                                    )}
                                    <div className="font-medium">
                                        {progress.mode === "import" ? "Import läuft …" : "Dry-Run läuft …"}
                                    </div>
                                </div>
                                <div className="h-3 bg-surface2 rounded-full overflow-hidden border border-border">
                                    <div
                                        className="h-full bg-tenant transition-all duration-300 ease-out"
                                        style={{
                                            width:
                                                progress.total > 0
                                                    ? `${Math.min(100, Math.round((progress.processed / progress.total) * 100))}%`
                                                    : "8%",
                                        }}
                                        data-testid="import-progress-bar"
                                    />
                                </div>
                                <div className="flex items-center justify-between text-xs text-muted-foreground tabular">
                                    <span data-testid="import-progress-text">
                                        {progress.total > 0
                                            ? `${progress.processed.toLocaleString("de-DE")} / ${progress.total.toLocaleString("de-DE")} Zeilen`
                                            : "Datei wird gelesen …"}
                                    </span>
                                    <span>
                                        {progress.total > 0
                                            ? `${Math.round((progress.processed / progress.total) * 100)} %`
                                            : ""}
                                    </span>
                                </div>
                                <p className="text-[11px] text-muted-foreground text-center italic">
                                    Bitte Fenster nicht schließen — der Job läuft im Hintergrund weiter.
                                </p>
                            </div>
                        </div>
                    )}
                    {step === 1 && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Wählen Sie zuerst den Zeitraum, für den die Daten gelten sollen.
                            </p>
                            <div>
                                <Label className="text-xs uppercase tracking-widest">Granularität</Label>
                                <Tabs value={zeitraumTyp} onValueChange={setZeitraumTyp} className="mt-2">
                                    <TabsList data-testid="import-granularitaet">
                                        <TabsTrigger value="jahr">Jahr</TabsTrigger>
                                        <TabsTrigger value="quartal">Quartal</TabsTrigger>
                                        <TabsTrigger value="monat">Monat</TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                            <div>
                                <Label htmlFor="zeitraum-input" className="text-xs uppercase tracking-widest">
                                    Zeitraum
                                </Label>
                                {zeitraumTyp === "jahr" && (
                                    <select
                                        id="zeitraum-input"
                                        value={zeitraum}
                                        onChange={(e) => setZeitraum(e.target.value)}
                                        className="mt-2 h-10 px-3 rounded-md border border-border bg-surface1 text-sm block"
                                        data-testid="import-zeitraum-value"
                                    >
                                        {yearOptions.map((y) => (
                                            <option key={y} value={String(y)}>
                                                {y}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {zeitraumTyp === "quartal" && (
                                    <Input
                                        id="zeitraum-input"
                                        className="mt-2 max-w-xs"
                                        value={zeitraum}
                                        onChange={(e) => setZeitraum(e.target.value)}
                                        placeholder="z.B. 2026-Q1"
                                        data-testid="import-zeitraum-value"
                                    />
                                )}
                                {zeitraumTyp === "monat" && (
                                    <Input
                                        id="zeitraum-input"
                                        className="mt-2 max-w-xs"
                                        value={zeitraum}
                                        onChange={(e) => setZeitraum(e.target.value)}
                                        placeholder="z.B. 2026-03"
                                        data-testid="import-zeitraum-value"
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Wählen Sie die Excel-Datei (.xlsx) mit den Bewegungsdaten.
                            </p>
                            <Card
                                className="border-dashed border-2 border-border p-10 text-center cursor-pointer hover:border-tenant transition-colors"
                                onClick={() => fileRef.current?.click()}
                                data-testid="import-dropzone"
                            >
                                <FileSpreadsheet className="h-10 w-10 mx-auto text-tenant" />
                                <div className="mt-3 text-sm">
                                    <span className="text-tenant font-medium">Datei wählen</span> oder hierher ziehen
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">.xlsx Format empfohlen</div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                    className="hidden"
                                    data-testid="import-file-input"
                                />
                            </Card>
                            {file && <div className="text-sm text-muted-foreground">Gewählt: {file.name}</div>}
                        </div>
                    )}

                    {step === 3 && preview && (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">
                                Ordnen Sie die Spalten Ihrer Datei den Feldern zu. Pflichtfelder sind markiert.
                            </p>
                            <div className="grid md:grid-cols-2 gap-3 max-h-[320px] overflow-auto pr-2">
                                {FIELDS.map((f) => (
                                    <div key={f.key} className="flex items-center gap-2">
                                        <Label className="w-44 text-xs">
                                            {f.label}
                                            {f.required && <span className="text-red-400"> *</span>}
                                        </Label>
                                        <select
                                            value={mapping[f.key] || ""}
                                            onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}
                                            className="flex-1 h-9 px-2 rounded-md border border-border bg-surface1 text-sm"
                                            data-testid={`map-${f.key}`}
                                        >
                                            <option value="">— nicht zugeordnet —</option>
                                            {preview.header.map((h) => (
                                                <option key={h} value={h}>
                                                    {h}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 4 && preview && (
                        <div className="space-y-3">
                            <p className="text-sm text-muted-foreground">Vorschau der ersten Zeilen Ihrer Datei:</p>
                            <div className="overflow-auto border border-border rounded-md max-h-[300px]">
                                <table className="text-xs w-full">
                                    <thead className="bg-surface2 sticky top-0">
                                        <tr>
                                            {preview.header.map((h) => (
                                                <th key={h} className="px-3 py-2 text-left uppercase tracking-widest text-muted-foreground">
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.preview.map((row, i) => (
                                            <tr key={i} className="border-t border-border">
                                                {row.map((c, j) => (
                                                    <td key={j} className="px-3 py-1.5">
                                                        {c}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Card className="p-4 bg-surface2 border-border">
                                <div className="text-xs uppercase tracking-widest text-muted-foreground">Importeinstellungen</div>
                                <div className="mt-1 text-sm">
                                    Zeitraum: <span className="font-medium">{zeitraum}</span> · Granularität:{" "}
                                    <span className="font-medium">{zeitraumTyp}</span> · Mandant:{" "}
                                    <span className="font-medium text-tenant">{activeTenant?.name}</span>
                                </div>
                            </Card>
                        </div>
                    )}

                    {step === 5 && dryRun && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-tenant">
                                <ShieldCheck className="h-5 w-5" />
                                <span className="font-medium">Dry-Run abgeschlossen – nichts wurde gespeichert</span>
                            </div>
                            {dryRun.verlagsbereiche_in_datei?.length > 0 && (
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                                        Erkannte Verlagsbereiche (Erlösgruppen)
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {dryRun.verlagsbereiche_in_datei.map((v) => (
                                            <span key={v} className="px-2 py-0.5 text-xs rounded-full bg-tenant-soft text-tenant">
                                                {v}
                                            </span>
                                        ))}
                                    </div>
                                </Card>
                            )}
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Würde importieren
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular text-emerald-400">
                                        {dryRun.imported_movements}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                        davon neu: {dryRun.neue_titel} · aktualisiert: {dryRun.aktualisierte_titel}
                                    </div>
                                </Card>
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Periode überschreiben
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular text-yellow-400">
                                        {dryRun.ueberschrieben_bestehende_periode}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                        bestehende {zeitraum}-Bewegung wird ersetzt
                                    </div>
                                </Card>
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Verworfen (Ebooks)
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular">
                                        {dryRun.verworfen_ebook}
                                    </div>
                                </Card>
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Neu & 0-Lager (verworfen)
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular">
                                        {dryRun.verworfen_neu_und_null_lager}
                                    </div>
                                    {dryRun.uebersprungen_juengere_bewegung?.length > 0 && (
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                            davon {dryRun.uebersprungen_juengere_bewegung.length} durch Option&nbsp;A
                                        </div>
                                    )}
                                </Card>
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Auf inaktiv (0-Lager)
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular text-orange-400">
                                        {dryRun.bestehende_auf_inaktiv.length}
                                    </div>
                                </Card>
                                <Card className="p-3 bg-surface2 border-border">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Auf inaktiv (fehlend)
                                    </div>
                                    <div className="font-heading text-xl mt-1 tabular text-orange-400">
                                        {dryRun.deaktivierte_titel_fehlend.length}
                                    </div>
                                </Card>
                            </div>
                            {dryRun.fehler_protokoll?.length > 0 && (
                                <Card className="p-3 bg-yellow-500/5 border-yellow-500/30">
                                    <div className="flex items-center gap-2 text-yellow-400 text-sm">
                                        <AlertCircle className="h-4 w-4" />
                                        Fehlerprotokoll: {dryRun.fehler_protokoll.length} Titel ohne HK-Wert
                                    </div>
                                </Card>
                            )}

                            {/* Detail-Liste: Durch Option A übersprungene Titel (0-Lager + jüngere Bewegung) */}
                            {dryRun.uebersprungen_juengere_bewegung?.length > 0 && (() => {
                                const rows = dryRun.uebersprungen_juengere_bewegung;
                                const generateExcel = async () => {
                                    if (!dryRunJobId) {
                                        toast.error("Kein Dry-Run-Job — bitte erst Dry-Run starten");
                                        return;
                                    }
                                    try {
                                        const r = await api.post(`/import/job/${dryRunJobId}/uebersprungen-excel`);
                                        const absUrl = `${window.location.origin}${r.data.url}`;
                                        setOptionAExport({
                                            url: absUrl,
                                            filename: r.data.filename,
                                            size: r.data.size,
                                        });
                                        const kb = (r.data.size / 1024).toFixed(1);
                                        toast.success(`Excel erstellt (${kb} KB) — jetzt herunterladen`);
                                    } catch (e) {
                                        toast.error(`Excel-Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannt"}`);
                                    }
                                };
                                return (
                                    <Card className="p-3 bg-sky-500/5 border-sky-500/30">
                                        <details open>
                                            <summary className="cursor-pointer text-sky-300 text-sm font-medium select-none">
                                                ℹ️ Durch Option A übersprungen: {rows.length} Titel
                                            </summary>
                                            <div className="mt-3 flex flex-wrap gap-2 items-center">
                                                {!optionAExport && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={generateExcel}
                                                        className="h-8 border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                                                        data-testid="option-a-excel-prepare"
                                                    >
                                                        <Download className="h-3.5 w-3.5 mr-1.5" /> Excel-Liste erzeugen
                                                    </Button>
                                                )}
                                                {optionAExport && (
                                                    <div className="w-full p-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                                                        <div className="text-emerald-300 text-xs font-medium">
                                                            ✓ Excel bereit ({(optionAExport.size / 1024).toFixed(1)} KB) — 15 Min. gültig
                                                        </div>
                                                        <div>
                                                            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                                                1. Diesen Link kopieren
                                                            </Label>
                                                            <div className="flex gap-2 mt-1">
                                                                <Input
                                                                    value={optionAExport.url}
                                                                    readOnly
                                                                    onFocus={(e) => e.target.select()}
                                                                    className="font-mono text-[11px] h-8 bg-surface1"
                                                                    data-testid="option-a-excel-url"
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    className="h-8 shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white"
                                                                    data-testid="option-a-excel-copy"
                                                                    onClick={async () => {
                                                                        try {
                                                                            await navigator.clipboard.writeText(optionAExport.url);
                                                                            toast.success("Link in Zwischenablage kopiert");
                                                                        } catch {
                                                                            toast.error("Bitte den Link manuell markieren und mit Strg+C kopieren");
                                                                        }
                                                                    }}
                                                                >
                                                                    Kopieren
                                                                </Button>
                                                            </div>
                                                        </div>
                                                        <div className="text-muted-foreground text-[11px] leading-relaxed">
                                                            <strong className="text-foreground">2.</strong> Neues Browser-Tab öffnen (Strg+T) →{" "}
                                                            <strong className="text-foreground">3.</strong> Link einfügen (Strg+V) →{" "}
                                                            <strong className="text-foreground">4.</strong> Enter. Browser lädt die Excel-Datei.
                                                        </div>
                                                        <details className="text-[11px]">
                                                            <summary className="cursor-pointer text-muted-foreground select-none">
                                                                Alternativ: Direkter Button (klappt nicht immer im Preview-Iframe)
                                                            </summary>
                                                            <div className="mt-2 flex flex-wrap gap-2">
                                                                <a
                                                                    href={optionAExport.url}
                                                                    download={optionAExport.filename}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-2 px-3 h-7 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-xs no-underline"
                                                                    data-testid="option-a-excel-download"
                                                                >
                                                                    <Download className="h-3 w-3" /> Excel herunterladen
                                                                </a>
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-7"
                                                                    onClick={() => setOptionAExport(null)}
                                                                >
                                                                    Neu erzeugen
                                                                </Button>
                                                            </div>
                                                        </details>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="mt-3 max-h-[300px] overflow-auto">
                                                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                                                    Diese 0-Lager-Zeilen werden ignoriert, weil bereits eine jüngere Bewegung existiert.
                                                </div>
                                                <ul className="text-xs space-y-0.5 font-mono">
                                                    {rows.map((t, i) => (
                                                        <li key={i} className="grid grid-cols-[140px_1fr_60px_80px] gap-2">
                                                            <span className="text-muted-foreground">{t.isbn}</span>
                                                            <span className="truncate" title={t.haupttitel}>{t.haupttitel || "—"}</span>
                                                            <span className="text-muted-foreground text-right">ET&nbsp;{t.et_jahr ?? "?"}</span>
                                                            <span className="text-muted-foreground text-right">→&nbsp;{t.jungere_periode ?? "?"}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </details>
                                    </Card>
                                );
                            })()}

                            {/* Detail-Listen für „Auf Inaktiv" — wichtig vor dem echten Import */}
                            {(dryRun.bestehende_auf_inaktiv?.length > 0 || dryRun.deaktivierte_titel_fehlend?.length > 0) && (
                                <Card className="p-3 bg-orange-500/5 border-orange-500/30">
                                    <details>
                                        <summary className="cursor-pointer text-orange-400 text-sm font-medium select-none">
                                            🔎 Welche Titel werden inaktiv? ({(dryRun.bestehende_auf_inaktiv?.length || 0) + (dryRun.deaktivierte_titel_fehlend?.length || 0)} insg.)
                                        </summary>
                                        <div className="mt-3 space-y-3 max-h-[300px] overflow-auto">
                                            {dryRun.bestehende_auf_inaktiv?.length > 0 && (
                                                <div>
                                                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                                                        0-Lager in der Import-Datei ({dryRun.bestehende_auf_inaktiv.length})
                                                    </div>
                                                    <ul className="text-xs space-y-0.5 font-mono">
                                                        {dryRun.bestehende_auf_inaktiv.map((t, i) => (
                                                            <li key={i} className="grid grid-cols-[140px_1fr] gap-2">
                                                                <span className="text-muted-foreground">{t.isbn}</span>
                                                                <span className="truncate" title={t.haupttitel}>{t.haupttitel || "—"}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                            {dryRun.deaktivierte_titel_fehlend?.length > 0 && (
                                                <div>
                                                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                                                        Nicht in Import enthalten ({dryRun.deaktivierte_titel_fehlend.length})
                                                    </div>
                                                    <ul className="text-xs space-y-0.5 font-mono">
                                                        {dryRun.deaktivierte_titel_fehlend.map((t, i) => (
                                                            <li key={i} className="grid grid-cols-[140px_1fr] gap-2">
                                                                <span className="text-muted-foreground">{t.isbn}</span>
                                                                <span className="truncate" title={t.haupttitel}>{t.haupttitel || "—"}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </details>
                                </Card>
                            )}

                            <p className="text-xs text-muted-foreground italic">
                                Wenn die Zahlen plausibel aussehen, klicken Sie auf „Import wirklich ausführen". Sonst:
                                „Zurück" und Spalten-Mapping prüfen.
                            </p>
                        </div>
                    )}

                    {step === 6 && result && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-emerald-400">
                                <CheckCircle2 className="h-5 w-5" />
                                <span className="font-medium">Import erfolgreich abgeschlossen</span>
                            </div>
                            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Bewegungen importiert</div>
                                    <div className="font-heading text-2xl mt-1 tabular text-emerald-400">{result.imported_movements}</div>
                                </Card>
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Neue Titel</div>
                                    <div className="font-heading text-2xl mt-1 tabular">{result.neue_titel}</div>
                                </Card>
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Aktualisiert</div>
                                    <div className="font-heading text-2xl mt-1 tabular">{result.aktualisierte_titel}</div>
                                </Card>
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Inaktiv (0-Lager)</div>
                                    <div className="font-heading text-2xl mt-1 tabular text-orange-400">{result.bestehende_auf_inaktiv.length}</div>
                                </Card>
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Inaktiv (fehlend)</div>
                                    <div className="font-heading text-2xl mt-1 tabular text-orange-400">{result.deaktivierte_titel_fehlend.length}</div>
                                </Card>
                                <Card className="p-4 bg-surface2 border-border">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Ebooks verworfen</div>
                                    <div className="font-heading text-2xl mt-1 tabular">{result.verworfen_ebook}</div>
                                </Card>
                            </div>
                            {result.fehler_protokoll?.length > 0 && (
                                <Card className="p-4 bg-yellow-500/5 border-yellow-500/30">
                                    <div className="flex items-center gap-2 text-yellow-400">
                                        <AlertCircle className="h-4 w-4" /> Fehlerprotokoll ({result.fehler_protokoll.length})
                                    </div>
                                    <ul className="mt-2 text-xs space-y-1 max-h-32 overflow-auto">
                                        {result.fehler_protokoll.map((f, i) => (
                                            <li key={i}>
                                                <span className="font-mono">{f.isbn}</span> — {f.haupttitel} · {f.grund}
                                            </li>
                                        ))}
                                    </ul>
                                </Card>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 backdrop-blur-sm border-t border-border z-20">
                    {step > 1 && step < 6 && step !== 5 && (
                        <Button variant="ghost" onClick={() => setStep(step - 1)} disabled={busy} data-testid="wizard-back">
                            Zurück
                        </Button>
                    )}
                    {step === 5 && (
                        <Button variant="ghost" onClick={() => setStep(3)} disabled={busy} data-testid="wizard-back-mapping">
                            Mapping korrigieren
                        </Button>
                    )}
                    {step === 5 && (
                        <Button
                            variant="outline"
                            onClick={reset}
                            disabled={busy}
                            data-testid="wizard-restart"
                            className="border-border text-muted-foreground hover:text-foreground"
                        >
                            Neu starten
                        </Button>
                    )}
                    {step === 1 && (
                        <Button
                            onClick={() => setStep(2)}
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="wizard-next"
                        >
                            Weiter
                        </Button>
                    )}
                    {step === 3 && (
                        <Button
                            onClick={() => setStep(4)}
                            disabled={!requiredOk || busy}
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="wizard-next-preview"
                        >
                            Weiter zur Vorschau
                        </Button>
                    )}
                    {step === 4 && (
                        <Button
                            onClick={runDryRun}
                            disabled={busy}
                            className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                            data-testid="wizard-dryrun"
                        >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            {busy ? "Validiere…" : "Dry-Run starten"}
                        </Button>
                    )}
                    {step === 5 && (
                        <Button
                            onClick={runImport}
                            disabled={busy}
                            className="bg-red-500 hover:bg-red-600 text-white"
                            data-testid="wizard-commit"
                        >
                            <PlayCircle className="h-4 w-4 mr-2" />
                            {busy ? "Importiere…" : "Import wirklich ausführen"}
                        </Button>
                    )}
                    {step === 6 && (
                        <Button onClick={() => close(false)} className="bg-tenant text-[color:var(--tenant-accent-foreground)]" data-testid="wizard-close">
                            Fertig
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
